/*----------------------------------
- TLD HUB PAGE
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0063
 * @rule The shared TLD board on the website: every root TLD with its registry facts, launch dates
 *       and latest change, figures and a band on the public set only. The rows arrive in the
 *       board's neutral order (the public set in seed order, then A to Z) and a visitor may order
 *       them on registry facts only; a figure outside the public set is a text-free blurred bar
 *       that opens the signup wall, in the list and in the side panel's peek and compare (one
 *       trigger, `website_tld_hub_locked_row`, its section saying where). The Movers are public-set
 *       TLDs in seed order, picked by Product. The board reads at the agenda's 1500px measure, so
 *       its full column tier fits beside the side panel (founder decision of 2026-10-03); the
 *       sections under it keep the page column.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect, useMemo } from 'preact/hooks';
import { AGENDA_REVEAL_DAY } from '@uniquedomains/components/contracts/agenda/public';
import { TLD_HUB_PATH, type PublicTldBoard } from '@uniquedomains/components/contracts/tlds/public';
import TldBoard from '@uniquedomains/components/tlds/board/TldBoard';
import type { TldBoardLockedSection } from '@uniquedomains/components/tlds/board/boardTypes';
import TldPagePill from '@uniquedomains/components/tlds/page/TldPagePill';
import { AGENDA_PATH } from '@uniquedomains/core/domains/tlds';
import { AGENDA_NEWSLETTER_BAND_CONTENT } from '@/client/catalogs/landing/agenda/pageContent';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildTldHubGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import NewsletterInkBand from '@/client/pages/landing/components/newsletter/NewsletterInkBand';
import { PAGE_CONTAINER_CLASS, WIDE_PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { buildProductPageConversionContext } from '@/client/services/conversion/intentContext';
import { buildWebsiteAuthHref, openWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { PRODUCT_PAGE_ROUTES } from '@/common/catalogs/appRoutesCatalog';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';
import { resolvePublishedTldHref, resolveTldHref } from '../../lib/presentation';
import { usePublishedTldPaths } from '../../lib/usePublishedTldPaths';
import { useTldGithubIntent } from '../../lib/useTldGithubIntent';
import TldPersonasCard from '../TldPersonasCard';
import '../../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type TldHubPageProps = {
    board: PublicTldBoard;
    navigation?: LandingHeaderDomainsNavigation | null;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'tld_hub';
const SUBSCRIBE_ANCHOR_ID = 'subscribe';
/** Signup attribution for every CTA of the page (`User.desiredFrom`). */
const HUB_ATTRIBUTION = 'website_tld_hub';

/** Every figure is in Product's `/tlds`: the title pill and the phone bar open this. */
const UNLOCK_HREF = buildWebsiteAuthHref({
    flow: 'cold',
    mode: 'signup',
    from: HUB_ATTRIBUTION,
    returnTo: PRODUCT_PAGE_ROUTES.tlds,
    context: buildProductPageConversionContext(TLD_PAGE_CONTENT.hub.board.title),
});

/**
 * A locked cell returns to Product's `/tlds` like the title pill, and names its row's ending in
 * the page context: a new account's brief and radar start on the ending the visitor chose
 * instead of losing it (ADR-0083 prerequisite A1, `resolveSearchProofFilters`).
 */
const buildLockedRowHref = (slug: string): string =>
    buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        from: HUB_ATTRIBUTION,
        returnTo: PRODUCT_PAGE_ROUTES.tlds,
        context: buildProductPageConversionContext(TLD_PAGE_CONTENT.hub.board.title, { tld: slug }),
    });

/** Where a locked figure was clicked: a row of the list, or the side panel's peek and compare. */
const LOCKED_SECTIONS: Readonly<Record<TldBoardLockedSection, string>> = { row: 'tld_hub_locked_row', peek: 'tld_hub_peek' };

/** Reveal Day's card opens the applications list. */
const REVEAL_DAY = { ...AGENDA_REVEAL_DAY, href: AGENDA_REVEAL_DAY.path };

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldHubPage({ board, navigation }: TldHubPageProps) {
    const content = TLD_PAGE_CONTENT.hub;
    const publishedPaths = usePublishedTldPaths();
    const publicSlugs = useMemo(() => new Set(board.rows.filter((row) => row.inPublicSet).map((row) => row.slug)), [board.rows]);
    const relatedLinks = getHumanPageRelationships(TLD_HUB_PATH).map((relationship) => ({
        label: relationship.label,
        href: relationship.targetPath,
    }));

    useEffect(() => {
        track('page_view', { page: TRACKING_PAGE, path: window.location.pathname });
    }, []);
    // The retired spokes of TLDs outside the public set 301 here with their query (D8), so a
    // GitHub dataset link still opens its signup. No TLD and no repository are known here.
    useTldGithubIntent(null, null);

    const trackUnlock = (section: string) => () =>
        trackConversionTrigger('website_tld_hub_unlock', { href: UNLOCK_HREF, page: TRACKING_PAGE, section });

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation ?? EMPTY_LANDING_NAVIGATION}
                    currentPath={TLD_HUB_PATH}
                    trackingPage={TRACKING_PAGE}
                    mobileActions={{
                        secondary: { label: content.mobileActions.agenda, href: AGENDA_PATH, iconName: 'calendar', testId: 'tld-hub-mobile-agenda' },
                        primary: {
                            label: content.mobileActions.unlock,
                            href: UNLOCK_HREF,
                            onClick: trackUnlock('tld_hub_mobile_bar'),
                            testId: 'tld-hub-mobile-unlock',
                        },
                    }}
                />
                <main id="top" data-testid="tld-hub">
                    <div class={`${WIDE_PAGE_CONTAINER_CLASS} pt-7 lg:pt-11`}>
                        <TldBoard
                            surface="website"
                            testIdPrefix="tld-hub"
                            titleVisibility="always"
                            rows={board.rows}
                            nextUp={board.nextUp}
                            movers={board.movers}
                            today={board.today}
                            revealDay={REVEAL_DAY}
                            copy={content.board}
                            hrefs={{
                                row: (row) => (row.inPublicSet ? resolvePublishedTldHref(row.slug, publishedPaths) : null),
                                nextUp: (next) => resolveTldHref(next.slug, publicSlugs, publishedPaths),
                                agenda: AGENDA_PATH,
                            }}
                            onRowClick={(row, href) => track('tld_hub_row_click', { tld: row.slug, section: 'board', href })}
                            // Beside the side panel a row click peeks instead of opening the page: the same
                            // event, so the interest in a row is still counted there.
                            onPeek={(row) => track('tld_hub_row_click', { tld: row.slug, section: 'peek' })}
                            onNextUpClick={(slug, href) => track('tld_hub_row_click', { ...(slug ? { tld: slug } : {}), section: 'next_up', href })}
                            onLockedClick={(row, section) => {
                                const href = buildLockedRowHref(row.slug);
                                trackConversionTrigger('website_tld_hub_locked_row', {
                                    href,
                                    page: TRACKING_PAGE,
                                    section: LOCKED_SECTIONS[section],
                                    context: { tld: row.slug },
                                });
                                openWebsiteAuthHref(href);
                            }}
                            titleActions={
                                <TldPagePill tone="ink" arrow href={UNLOCK_HREF} onClick={trackUnlock('tld_hub_title')} testId="tld-hub-unlock" attribution={HUB_ATTRIBUTION}>
                                    {content.unlock}
                                </TldPagePill>
                            }
                        />
                    </div>
                    <div class={`${PAGE_CONTAINER_CLASS} pt-10 lg:pt-14`}>
                        <TldPersonasCard
                            testId="tld-hub-conversion"
                            attribution={HUB_ATTRIBUTION}
                            investorTriggerId="website_tld_hub_investor"
                            founderTriggerId="website_tld_hub_founder"
                            trackingPage={TRACKING_PAGE}
                            section="tld_hub_personas"
                            surface="tld_hub"
                        />
                    </div>
                    <div class={`${PAGE_CONTAINER_CLASS} pt-6 lg:pt-8`}>
                        <NewsletterInkBand
                            anchorId={SUBSCRIBE_ANCHOR_ID}
                            source="website_tld_hub"
                            heading={AGENDA_NEWSLETTER_BAND_CONTENT.heading}
                            body={AGENDA_NEWSLETTER_BAND_CONTENT.body}
                        />
                    </div>
                    <RelatedLinks links={relatedLinks} title={content.relatedLinksTitle} />
                    <PageGeoBlock className="pb-4" geo={buildTldHubGeoContext()} surface="tld_hub" />
                </main>
                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
            </div>
        </div>
    );
}
